import React, { useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import { FortumLandingPageLogo } from '../fortum-landing-page-logo'
import { ChargeAmpsLandingPageLogo } from '../charge-amps-landing-page-logo'
import { LandingPageFooter } from '../landing-page-footer'
import currentDomain, { SiteDomain } from '../currentDomain'
import { LanguageSelectDrawer } from '../language-select-drawer'
import { currentCompany, Company } from '../current-company'

const SWrapper = styled.div<{ dark?: boolean; background?: string }>`
    font-family: ${({ theme }) => theme.font.name};
    min-height: 100vh;
    width: 100%;
    color: ${({ theme, dark }) => (dark ? theme.font.lightestColor : theme.font.darkestColor)};
    background: ${({ theme, background, dark }) =>
        background ? background : dark ? theme.darkBackground : theme.lightBackground};
    background-size: cover;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

const SChargeAmpsLandingPageLogo = styled(ChargeAmpsLandingPageLogo)<{ dark?: boolean }>`
    fill: ${({ theme, dark }) => (dark ? theme.font.lightestColor : theme.font.darkestColor)};
`

const SHeader = styled.div`
    padding: 47px 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 600;

    @media (max-width: 576px) {
        padding: 31px 8px;
    }
`

const SBody = styled.div`
    display: flex;
    justify-content: center;

    @media screen and (max-width: 600px) {
        margin-bottom: 50px;
    }
`

export const SContentWrapper = styled.div`
    width: 100%;
    max-width: 450px;
    font-size: 16px;
    padding: 0 10px;
`

export const SSpacer = styled.div`
    margin-bottom: 40px;
`

export const SNotification = styled.div<{ error?: boolean }>`
    display: block;
    width: 100%;
    padding: 14px;
    text-align: center;
    font-size: 14px;
    background-color: ${({ error }) => (error ? '#FF8B7C' : '#81E18B')};
    border-radius: 6px;
`

export type LandingPageLayoutProps = {
    background?: string
    children: React.ReactNode
    dark?: boolean
    className?: ClassName
    style?: React.CSSProperties
    hideLanguageSelect?: boolean
    headerContent?: React.ReactNode
}

export function LandingPageLayout({
    children,
    className,
    style,
    background: customBackground,
    dark,
    hideLanguageSelect = false,
    headerContent,
}: LandingPageLayoutProps): React.ReactElement {
    const background = useMemo(() => {
        if (customBackground) return customBackground
        return currentCompany === Company.FORTUM &&
            (currentDomain === SiteDomain.ADMIN || currentDomain === SiteDomain.PARTNER)
            ? '#6ebf7d'
            : ''
    }, [customBackground])

    return (
        <SWrapper className={cn(className)} style={style} background={background} dark={dark}>
            <SHeader>
                {currentCompany === Company.FORTUM &&
                (currentDomain === SiteDomain.ADMIN || currentDomain === SiteDomain.PARTNER) ? (
                    <FortumLandingPageLogo data-testid="fortumLogo" />
                ) : (
                    <div data-testid="caLogo">
                        <SChargeAmpsLandingPageLogo dark={dark} />
                    </div>
                )}

                {headerContent}

                {!hideLanguageSelect && <LanguageSelectDrawer dark={dark} />}
            </SHeader>
            <SBody>{children}</SBody>
            <div>{currentCompany === Company.CHARGE_AMPS && <LandingPageFooter dark={dark} />}</div>
        </SWrapper>
    )
}
